import { logger } from '@vagabondinternet/logging';
import { findRules } from '@words-happen/board-rules-engine-client';
import type { DictionaryModule } from '@words-happen/dictionary';
import { BoardGameScreen } from '@words-happen/screens';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { type BoardGameApi, createBoardGameApi } from '../../api/games';
import { useDictionary } from '../../dictionary';
import type { PageVisibility } from '../../games/page-visibility';
import { useBoardGame } from '../../games/use-board-game';
import { ROUTES } from '../../routes';
import { useSession } from '../../session';

export interface GamePageProps {
  /** Replaces words-happen-gql's board game API. */
  boardGameApi?: BoardGameApi;
  /** Replaces the 5-second poll. */
  pollMs?: number;
  /** Replaces the browser's page visibility. */
  visibility?: PageVisibility;
  /** Replaces the word list's lazy load. Keep it the same function across renders. */
  loadDictionary?: () => Promise<DictionaryModule>;
}

/** The game's word lookup for the score preview: none until the list arrives, or for a version this build lacks. */
const useIsWord = (rulesVersion: number | undefined, load?: () => Promise<DictionaryModule>) => {
  const dictionary = useDictionary(load);
  return useMemo(() => {
    const version = rulesVersion === undefined ? undefined : findRules(rulesVersion)?.DICTIONARY_VERSION;
    if (dictionary.status !== 'ready' || version === undefined) return undefined;
    try {
      return dictionary.dictionary.dictionaryFor(version).isWord;
    } catch (error) {
      logger.warn({ message: 'No dictionary for the game', version, error: (error as Error).message });
      return undefined;
    }
  }, [dictionary, rulesVersion]);
};

const GameView: React.FC<Required<Pick<GamePageProps, 'boardGameApi'>> & GamePageProps & { gameId: string }> = ({
  boardGameApi,
  gameId,
  pollMs,
  visibility,
  loadDictionary,
}) => {
  const navigate = useNavigate();
  const { status, game, notice, refresh, play, pass, swap } = useBoardGame(boardGameApi, gameId, {
    pollMs,
    visibility,
  });
  const isWord = useIsWord(game?.rulesVersion, loadDictionary);

  return (
    <BoardGameScreen
      status={status}
      game={game}
      isWord={isWord}
      notice={notice}
      onPlay={play}
      onPass={pass}
      onSwap={swap}
      onRetryLoad={refresh}
      onBack={() => navigate(ROUTES.games)}
      onNewGame={() => navigate(ROUTES.newGame)}
    />
  );
};

/**
 * A two-player board game, /games/:id (WH-156): words-happen-gql's game query as the player sees it, polled while the
 * opponent is to move (useBoardGame), and the playMove, passTurn and swapTiles moves. The dictionary loads with the
 * screen, for the score preview. Signed out, it goes to /welcome.
 */
export const GamePage: React.FC<GamePageProps> = ({ boardGameApi, ...options }) => {
  const { signedIn } = useSession();
  const { id = '' } = useParams();
  const [api] = useState(() => boardGameApi ?? createBoardGameApi());

  if (!signedIn) return <Navigate replace to={ROUTES.welcome} />;
  return <GameView key={id} boardGameApi={api} gameId={id} {...options} />;
};
